import { BUSINESS_TYPE } from "@faire/deprecated-retailer-visitor-shared/consts/BUSINESS_TYPE";
import {
  AddAddress,
  AddCollectionToCart,
  AddToCart,
  BrandBlogContentFormSubmission,
  IAddCollectionToCartEvent,
  IAddToCartEvent,
  IBrandBlogContentSubmission,
  IRetailerPurchaseEvent,
  IUserAndRetailer,
  MakerAdPageApply,
  MakerSubmitApplication,
  OrderSubmitted,
  SignUp,
  SignUpPersonalInfoNextPressed,
  SignUpDescribeStoreNextPressed,
  SignUpStoreDetailNextPressed,
  ViewBrand,
  ViewProduct,
  IPersonalInfoSubmission,
  IStoreDetailsSubmission,
  IDescribeStoreSubmission,
  OpenWithFairePageView,
  OpenWithFairePageYesFundingSelected,
  OpenWithFaireSignUpApplicationView,
  OpenWithFaireFinancialSectionNextPressed,
  OpenWithFaireStoreInfoSectionNextPressed,
  OpenWithFaireOwnershipSectionNextPressed,
  OpenWithFaireAdditionalSectionNextPressed,
} from "@faire/deprecated-retailer-visitor-shared/events";
import {
  groupWithAddedProductItems,
  IProductItem,
} from "@faire/deprecated-retailer-visitor-shared/lib/cartTools";
import { GoogleTagManager } from "@faire/deprecated-retailer-visitor-shared/lib/googleTagManager";
import { getProductOptionWholesalePrice } from "@faire/deprecated-retailer-visitor-shared/lib/product";
import { getIsProduction } from "@faire/deprecated-retailer-visitor-shared/serialized-data/getEnvName";
import { getRetailer } from "@faire/deprecated-retailer-visitor-shared/serialized-data/getRetailer";
import { IBrand } from "@faire/web-api--source/indigofair/data/IBrand";
import { IBrandLead } from "@faire/web-api--source/indigofair/data/IBrandLead";
import { IProduct } from "@faire/web-api--source/indigofair/data/IProduct";
import { singletonGetter } from "@faire/web/common/singletons/getter";
import map from "lodash/map";
import sumBy from "lodash/sumBy";

/**
 * @deprecated prefer to use `useGoogleTagManagerEvents` or `withStores` instead
 */
export class GoogleTagManagerEvents {
  static get = singletonGetter(GoogleTagManagerEvents);

  private gtm = new GoogleTagManager(
    getIsProduction() ? "GTM-58SPJVF" : "GTM-5QQZCRL"
  );

  constructor() {
    this.updateVariableUserId();
    this.gtm.pushEvent("if-gtm-init");

    SignUp.subscribe(this.handleSignUp);
    SignUpPersonalInfoNextPressed.subscribe(
      this.handleSignUpPersonalNextClicked
    );
    SignUpStoreDetailNextPressed.subscribe(
      this.handleSignUpStoreDetailNextClicked
    );
    SignUpDescribeStoreNextPressed.subscribe(
      this.handleSignUpDescribeStoreNextClicked
    );
    ViewProduct.subscribe(this.handleViewProduct);
    ViewBrand.subscribe(this.handleViewBrand);
    AddAddress.subscribe(this.handleAddAddress);
    OrderSubmitted.subscribe(this.handleOrderSubmitted);
    AddToCart.subscribe(this.handleAddToCart);
    AddCollectionToCart.subscribe(this.handleAddCollectionToCart);

    // Maker Events
    BrandBlogContentFormSubmission.subscribe(
      this.handleBrandBlogContentFormSubmission
    );
    MakerSubmitApplication.subscribe(this.handleMakerApplication);
    MakerAdPageApply.subscribe(this.handleMakerApplication);

    //owf events
    OpenWithFairePageView.subscribe(this.handleOpenWithFairePageView);
    OpenWithFairePageYesFundingSelected.subscribe(
      this.handleOpenWithFairePageYesFundingSelected
    );
    OpenWithFaireSignUpApplicationView.subscribe(
      this.handleOpenWithFaireSignUpApplicationView
    );
    OpenWithFaireFinancialSectionNextPressed.subscribe(
      this.handleOpenWithFaireFinancialSectionNextPressed
    );
    OpenWithFaireStoreInfoSectionNextPressed.subscribe(
      this.handleOpenWithFaireStoreInfoSectionNextPressed
    );
    OpenWithFaireOwnershipSectionNextPressed.subscribe(
      this.handleOpenWithFaireOwnershipSectionNextPressed
    );
    OpenWithFaireAdditionalSectionNextPressed.subscribe(
      this.handleOpenWithFaireAdditionalSectionNextPressed
    );
  }

  private handleSignUpPersonalNextClicked = (
    personalInfo: IPersonalInfoSubmission
  ) => {
    this.gtm.pushEvent("personal-info-next-click", { ...personalInfo });
  };

  private handleSignUpStoreDetailNextClicked = (
    storeDetails: IStoreDetailsSubmission
  ) => {
    this.gtm.pushEvent("store-detail-next-click", { ...storeDetails });
  };

  private handleSignUpDescribeStoreNextClicked = (
    describeStore: IDescribeStoreSubmission
  ) => {
    this.gtm.pushEvent("describe-store-next-click", { ...describeStore });
  };

  private handleOpenWithFairePageView = () => {
    this.gtm.pushEvent("open-with-faire-page-view");
  };

  private handleOpenWithFairePageYesFundingSelected = () => {
    this.gtm.pushEvent("open-with-faire-page-yes-funding-selected");
  };

  private handleOpenWithFaireSignUpApplicationView = () => {
    this.gtm.pushEvent("open-with-faire-combined-application-view");
  };

  private handleOpenWithFaireFinancialSectionNextPressed = () => {
    this.gtm.pushEvent("open-with-faire-combined-financial-next-pressed");
  };

  private handleOpenWithFaireStoreInfoSectionNextPressed = () => {
    this.gtm.pushEvent("open-with-faire-combined-store-info-next-pressed");
  };

  private handleOpenWithFaireOwnershipSectionNextPressed = () => {
    this.gtm.pushEvent("open-with-faire-combined-ownership-next-pressed");
  };

  private handleOpenWithFaireAdditionalSectionNextPressed = () => {
    this.gtm.pushEvent("open-with-faire-combined-additional-next-pressed");
  };

  private get isBrickAndMortar() {
    return getRetailer()?.business_type === BUSINESS_TYPE.BRICK_AND_MORTAR_SHOP;
  }

  private get isOnlineOnlyRetailer() {
    return getRetailer()?.business_type === BUSINESS_TYPE.ONLINE_ONLY;
  }

  private updateVariableUserId = () => {
    const retailer = getRetailer();
    if (retailer) {
      this.gtm.pushVariables({ userId: retailer.token! });
    } else {
      this.gtm.pushVariables({ userId: undefined });
    }
  };

  private getContentsFromProductItems = (
    products: IProductItem[],
    eventType: { addToCart: boolean }
  ) => {
    return map(products, ({ product_token, quantity, price_cents }) => ({
      id: product_token,
      quantity,
      item_price:
        (eventType.addToCart ? price_cents / quantity : price_cents) / 100,
    }));
  };

  private handleAddAddress = () => {
    this.gtm.pushEvent("add-address");
  };

  private handleSignUp = ({ retailer }: IUserAndRetailer) => {
    this.gtm.pushEvent("create-an-account", {
      retailer_token: retailer.token ?? "",
      amountDollars: 1,
    });

    // Note at this point RetailerStore hasn't been updated, so don't use
    // this.isBrickAndMortar or this.isOnlineOnlyRetailer
    if (
      retailer.business_type === BUSINESS_TYPE.BRICK_AND_MORTAR_SHOP ||
      retailer.business_type === BUSINESS_TYPE.ONLINE_ONLY
    ) {
      this.gtm.pushEvent("if-online-or-brick-and-mortar-sign-up", {
        retailer_token: retailer.token ?? "",
      });
      if (retailer.business_type === BUSINESS_TYPE.BRICK_AND_MORTAR_SHOP) {
        this.gtm.pushEvent("if-brick-and-mortar-sign-up", {
          retailer_token: retailer.token ?? "",
        });
      } else if (retailer.business_type === BUSINESS_TYPE.ONLINE_ONLY) {
        this.gtm.pushEvent("if-online-sign-up", {
          retailer_token: retailer.token ?? "",
        });
      }
    }
  };

  private handleViewProduct = (product: IProduct) => {
    if (!product.token) {
      return;
    }

    this.gtm.pushEvent("page-view", {
      dynx_pagetype: "offerdetail",
      dynx_itemid: product.token,
      dynx_itemid2: product.brand_token,
      dynx_totalvalue: getProductOptionWholesalePrice(product).amount_cents,
    });

    this.gtm.pushEvent("if-product-view", {
      product_token: product.token,
      product_name: product.name,
      product_images: product.images,
      product_description: product.description,
      brand_token: product.brand_token,
    });
  };

  private handleViewBrand = (brand: Pick<IBrand, "token" | "is_internal">) => {
    if (brand.is_internal) {
      return;
    }

    this.gtm.pushEvent("page-view", {
      dynx_itemid2: brand.token,
      dynx_pagetype: "searchresults",
    });
  };

  private handleAddToCart = (event: IAddToCartEvent) => {
    const givenProductItems = groupWithAddedProductItems(event.added);

    if (!givenProductItems.length) {
      return;
    }

    const contents = this.getContentsFromProductItems(givenProductItems, {
      addToCart: true,
    });

    const totalItems = sumBy(givenProductItems, "quantity");

    this.gtm.pushEvent("if-add-to-cart", {
      // Used to report add to cart value to facebook
      amountCents: event.amountAdded,

      // Old variables, not sure what this is used for
      items: givenProductItems,
    });

    if (this.isBrickAndMortar || this.isOnlineOnlyRetailer) {
      this.gtm.pushEvent("if-online-or-brick-and-mortar-add-to-cart", {
        // Used to report add to cart value to facebook
        amountCents: event.amountAdded,

        content_type: "product",
        contents,
        num_items: totalItems,
      });
      if (this.isBrickAndMortar) {
        this.gtm.pushEvent("if-brick-and-mortar-add-to-cart", {
          // Used to report add to cart value to facebook
          amountCents: event.amountAdded,

          // Old variables, not sure what this is used for
          items: givenProductItems,
        });
      } else if (this.isOnlineOnlyRetailer) {
        this.gtm.pushEvent("if-online-add-to-cart", {
          // Used to report add to cart value to facebook
          amountCents: event.amountAdded,

          content_type: "product",
          contents,
          num_items: totalItems,
        });
      }
    }

    givenProductItems.forEach((item) => {
      this.gtm.pushEvent("add-products", {
        dynx_itemid: item.product_token,
        dynx_itemid2: item.brand_token,
        dynx_pagetype: "conversionintent",
        dynx_totalvalue: `${item.price_cents / 100}`,
      });
    });
  };

  private handleOrderSubmitted = (event: IRetailerPurchaseEvent) => {
    const { givenProductItems, retailer } = event;

    if (!givenProductItems.length) {
      return;
    }

    const amountCents = sumBy(
      givenProductItems,
      (item) => item.quantity * item.price_cents
    );
    const productTokens = givenProductItems.map((i) => i.product_token);
    const brandTokens = givenProductItems.map((i) => i.brand_token);
    const totalValues = givenProductItems.map(
      ({ quantity = 0, price_cents = 0 }) => (quantity * price_cents) / 100
    );
    const contents = this.getContentsFromProductItems(givenProductItems, {
      addToCart: false,
    });

    const totalItems = sumBy(givenProductItems, "quantity");

    this.gtm.pushEvent("checkout-order", {
      // Used to report add to cart value to facebook
      amountCents,

      // Old variables, not sure what this is used for
      dynx_itemid: productTokens,
      dynx_itemid2: brandTokens,
      dynx_pagetype: "conversion",
      dynx_totalvalue: totalValues,
    });

    if (this.isBrickAndMortar || this.isOnlineOnlyRetailer) {
      this.gtm.pushEvent("if-online-or-brick-and-mortar-checkout-order", {
        // Used to report add to cart value to facebook
        amountCents,

        content_type: "product",
        contents,
        num_items: totalItems,
      });
      if (this.isBrickAndMortar) {
        this.gtm.pushEvent("if-brick-and-mortar-checkout-order", {
          // Used to report add to cart value to facebook
          amountCents,

          // Old variables, not sure what this is used for
          items: givenProductItems,
        });
      } else if (this.isOnlineOnlyRetailer) {
        this.gtm.pushEvent("if-online-checkout-order", {
          // Used to report add to cart value to facebook
          amountCents,

          content_type: "product",
          contents,
          num_items: totalItems,
        });
      }
    }

    if (!retailer?.last_order_at) {
      this.gtm.pushEvent("if-first-purchase", {
        // Used to report add to cart value to facebook
        amountCents,

        // Old variables, not sure what this is used for
        items: givenProductItems,
      });
    }
  };

  private handleAddCollectionToCart = (event: IAddCollectionToCartEvent) => {
    this.gtm.pushEvent("if-add-to-cart-collection", {
      collection_token: event.collectionToken,
    });
  };

  private handleMakerApplication = (brandLead: IBrandLead) => {
    const numberOfStockists = brandLead.number_of_stockists ?? 0;
    if (numberOfStockists > 11) {
      this.gtm.pushEvent(
        "if-maker-complete-registration-greater-than-11-stockists",
        {
          brand_token: brandLead.brand_token,
        }
      );
    }

    this.gtm.pushEvent("if-maker-complete-registration", {
      brand_token: brandLead.brand_token,
    });
  };

  private handleBrandBlogContentFormSubmission = (
    submission: IBrandBlogContentSubmission
  ) => {
    const numberOfStockists = submission.stockists ?? 0;
    if (numberOfStockists > 11) {
      this.gtm.pushEvent(
        "if-brand-blog-content-form-submission-greater-than-11-stockists"
      );
    }

    this.gtm.pushEvent("if-brand-blog-content-form-submission");
  };
}
